import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  dayPrice, // optional (date, activeModifiers) => string | null — small price under each day
  ...props
}) {
  const withPrice = typeof dayPrice === "function";
  // Wider/taller cells when we show a price line under the day number.
  const cellSize = withPrice ? "h-12 w-11" : "h-8 w-8";
  const headWidth = withPrice ? "w-11" : "w-8";

  const components = {
    IconLeft: ({ className, ...p }) => <ChevronLeft className={cn("h-4 w-4", className)} {...p} />,
    IconRight: ({ className, ...p }) => <ChevronRight className={cn("h-4 w-4", className)} {...p} />,
  };
  if (withPrice) {
    components.DayContent = ({ date, activeModifiers }) => {
      const price = activeModifiers?.disabled || activeModifiers?.hidden ? null : dayPrice(date, activeModifiers);
      return (
        <div className="flex flex-col items-center justify-center leading-none">
          <span className="text-sm">{date.getDate()}</span>
          {price ? <span className="mt-0.5 text-[9px] font-semibold text-[#1fa5a3]">{price}</span> : null}
        </div>
      );
    };
  }

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          cn("text-muted-foreground rounded-md font-normal text-[0.8rem]", headWidth),
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          cellSize,
          "p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={components}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
